"use client";

import { Linkedin, Instagram, Youtube } from 'lucide-react';
import { WhatsAppIcon, TwitterIcon } from "@/components/dashboard/socials-dropup";
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { trackEvent } from '@/lib/mixpanel';

const Footer = () => {
    const pathname = usePathname();
    const isHomeRoute = pathname === '/';

    const theme = {
        bg: isHomeRoute ? "bg-transparent" : "bg-[#2563EB]",
        textPrimary: isHomeRoute ? "text-slate-900" : "text-white",
        textSecondary: isHomeRoute ? "text-slate-500" : "text-white/70",
        textSecondaryHover: isHomeRoute ? "hover:text-slate-900" : "hover:text-white",
        textMuted: isHomeRoute ? "text-slate-400" : "text-white/50",
        textMutedHover: isHomeRoute ? "hover:text-slate-600" : "hover:text-white",
        border: isHomeRoute ? "border-slate-200" : "border-white/10",
        iconBg: isHomeRoute ? "bg-slate-100" : "bg-white/10",
        iconText: isHomeRoute ? "text-slate-600" : "text-white/80",
        iconBgHover: isHomeRoute ? "hover:bg-slate-200" : "hover:bg-white",
        iconTextHover: isHomeRoute ? "hover:text-[#2563EB]" : "hover:text-[#2563EB]",
        logo: isHomeRoute ? "text-blue-950" : "text-white",
    };

    return (
        <div className="bg-transparent pb-4" style={{ paddingTop: "16px" }}>
            <footer className={`${theme.bg} rounded-[32px] pt-16 pb-8 font-jakarta overflow-hidden mx-auto max-w-[1490px]`}>
                <div className="mx-auto px-6 lg:px-8" style={{ maxWidth: "1490px", paddingBottom: "2rem" }}>
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-y-12 gap-x-8">

                        {/* Brand Column */}
                        <div className="col-span-2 lg:col-span-2 pr-8">
                            <div className="flex flex-col mb-4">
                                <Link href="/" target="_blank" rel="noopener noreferrer" className={`${theme.logo} font-bold text-xl tracking-tight leading-none mb-1`}>
                                    Deckwale
                                </Link>
                                <span className={`text-[12px] font-semibold ${isHomeRoute ? "text-blue-600" : "text-white/70"}`}>by Seedicon</span>
                            </div>

                            <p className={`${theme.textPrimary} text-[13px] leading-relaxed max-w-xs font-medium`}>
                                Empowering business storytelling with AI-driven presentations and data tools.
                            </p>
                            <div className="flex gap-4 mt-6">
                                {[
                                    { icon: TwitterIcon, href: "https://x.com/Deckwale", label: "Twitter" },
                                    { icon: Linkedin, href: "https://www.linkedin.com/company/deckwale", label: "LinkedIn" },
                                    { icon: Instagram, href: "https://www.instagram.com/deckwale/", label: "Instagram" },
                                    { icon: Youtube, href: "https://www.youtube.com/@DeckwaleAI", label: "YouTube" },
                                    { icon: WhatsAppIcon, href: "https://chat.whatsapp.com/B9gMJ4FvMNHGIgKQMux1zq", label: "WhatsApp" }
                                ].map((item, idx) => (
                                    <a
                                        key={idx}
                                        href={item.href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label={item.label}
                                        onClick={() => trackEvent("Footer Link Clicked", { label: item.label, href: item.href, category: "Social" })}
                                        className={`w-8 h-8 rounded-full ${theme.iconBg} flex items-center justify-center ${theme.iconText} ${theme.iconBgHover} ${theme.iconTextHover} transition-all duration-300`}
                                    >
                                        <item.icon size={item.label === "WhatsApp" ? 14 : 16} className={item.label === "WhatsApp" ? "w-3.5 h-3.5" : (item.label === "Twitter" ? "w-4 h-4" : "")} />
                                    </a>
                                ))}
                            </div>
                        </div>

                        {/* Features Column 1 */}
                        <div>
                            <h4 role="presentation" className={`font-jakarta font-bold ${theme.textPrimary} text-xs uppercase tracking-wider mb-6`}>Features</h4>
                            <ul className="space-y-1.5">
                                {[
                                    { label: "Deep Research", path: "/features/deep-research" },
                                    { label: "Presentation Outline", path: "/features/presentation-outline" },
                                    { label: "AI Content Manipulation", path: "/features/ai-content-manipulation" },
                                    { label: "Multilingual Presentation", path: "/features/multilingual-presentation" },
                                    { label: "Brand-Aligned Theme", path: "/features/brand-aligned-theme" },
                                    { label: "Smart AI Editing", path: "/features/smart-ai-editing" },
                                ].map((item) => (
                                    <li key={item.label}>
                                        <Link 
                                            href={item.path} 
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={() => trackEvent("Footer Link Clicked", { label: item.label, href: item.path, category: "Features" })} 
                                            className={`text-[13px] ${theme.textSecondary} ${theme.textSecondaryHover} transition-colors`}
                                        >
                                            {item.label}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Features Column 2 */}
                        <div>
                            <h4 role="presentation" className={`font-jakarta font-bold ${theme.textPrimary} text-xs uppercase tracking-wider mb-6`}>Features</h4>
                            <ul className="space-y-1.5">
                                {[
                                    { label: "Image Generation", path: "/features/image-generation" },
                                    { label: "Collaborative Access", path: "/features/collaborative-access" },
                                    { label: "Pitch Preparation Q&A", path: "/features/pitch-preparation" },
                                    { label: "Deep Analytics", path: "/features/deep-analytics" },
                                    { label: "Multiple Download Formats", path: "/features/multiple-formats" }
                                ].map((item) => (
                                    <li key={item.label}>
                                        <Link 
                                            href={item.path} 
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={() => trackEvent("Footer Link Clicked", { label: item.label, href: item.path, category: "Features" })} 
                                            className={`text-[13px] ${theme.textSecondary} ${theme.textSecondaryHover} transition-colors`}
                                        >
                                            {item.label}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Products Column */}
                        <div>
                            <h4 role="presentation" className={`font-jakarta font-bold ${theme.textPrimary} text-xs uppercase tracking-wider mb-6`}>Products</h4>
                            <ul className="space-y-1.5">
                                {[
                                    { label: "Presentation Builder", path: "/products/presentation-builder" },
                                    { label: "Fundraising Pitch Deck", path: "/products/fundraising-pitch-deck" },
                                    { label: "Business Report", path: "/products/business-report" },
                                    { label: "Cap Table Structure", path: "/products/cap-table-structure" },
                                    { label: "Partnership Proposal", path: "/products/partnership-proposal-builder" },
                                    { label: "Team Introduction", path: "/products/team-introduction" }
                                ].map((item) => (
                                    <li key={item.label}>
                                        <Link
                                            href={item.path}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={() => trackEvent("Footer Link Clicked", { label: item.label, href: item.path, category: "Products" })}
                                            className={`text-[13px] ${theme.textSecondary} ${theme.textSecondaryHover} transition-colors`}
                                        >
                                            {item.label}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Use Cases Column */}
                        <div>
                            <h4 role="presentation" className={`font-jakarta font-bold ${theme.textPrimary} text-xs uppercase tracking-wider mb-6`}>Use Cases</h4>
                            <ul className="space-y-1.5">
                                {[
                                    "Startups", "Enterprise", "Sales Teams", "Students", "Investment Bankers"
                                ].map((item) => (
                                    <li key={item}>
                                        <Link 
                                            href="/use-cases" 
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={() => trackEvent("Footer Link Clicked", { label: item, href: "/use-cases", category: "Use Cases" })} 
                                            className={`text-[13px] ${theme.textSecondary} ${theme.textSecondaryHover} transition-colors`}
                                        >
                                            {item}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Company Column */}
                        <div>
                            <h4 role="presentation" className={`font-jakarta font-bold ${theme.textPrimary} text-xs uppercase tracking-wider mb-6`}>Company</h4>
                            <ul className="space-y-1.5">
                                <li><Link href="/aboutus" target="_blank" rel="noopener noreferrer" onClick={() => trackEvent("Footer Link Clicked", { label: "About Us", href: "/aboutus", category: "Company" })} className={`text-[13px] ${theme.textSecondary} ${theme.textSecondaryHover} transition-colors`}>About Us</Link></li>
                                <li><Link href="/contact" target="_blank" rel="noopener noreferrer" onClick={() => trackEvent("Footer Link Clicked", { label: "Contact Us", href: "/contact", category: "Company" })} className={`text-[13px] ${theme.textSecondary} ${theme.textSecondaryHover} transition-colors`}>Contact Us</Link></li>
                                <li><Link href="/pricing" target="_blank" rel="noopener noreferrer" onClick={() => trackEvent("Footer Link Clicked", { label: "Pricing", href: "/pricing", category: "Company" })} className={`text-[13px] ${theme.textSecondary} ${theme.textSecondaryHover} transition-colors`}>Pricing</Link></li>
                                <li><Link href="/faqs" target="_blank" rel="noopener noreferrer" onClick={() => trackEvent("Footer Link Clicked", { label: "FAQs", href: "/faqs", category: "Company" })} className={`text-[13px] ${theme.textSecondary} ${theme.textSecondaryHover} transition-colors`}>FAQs</Link></li>
                                <li><Link href="/resources" target="_blank" rel="noopener noreferrer" onClick={() => trackEvent("Footer Link Clicked", { label: "Resources", href: "/resources", category: "Company" })} className={`text-[13px] ${theme.textSecondary} ${theme.textSecondaryHover} transition-colors`}>Resources</Link></li>
                            </ul>
                        </div>
                    </div>
                </div>

                {/* Brand Text - Full Width */}


                {/* Copyright Section - Constrained Width & Bordered */}
                <div className="mx-auto px-6 lg:px-8" style={{ maxWidth: "1440px" }}>
                    <div className={`border-t ${theme.border} pt-8 flex flex-col md:flex-row justify-between items-center gap-4`}>
                        <p className={`text-[14px] ${theme.textMuted} order-2 md:order-1`}>
                            © 2026 Deckwale. All Rights Reserved.
                        </p>
                        <div className="flex flex-wrap justify-center md:justify-end gap-6 order-1 md:order-2">
                            <Link href="/privacy-policy" target="_blank" rel="noopener noreferrer" onClick={() => trackEvent("Footer Link Clicked", { label: "Privacy Policy", href: "/privacy-policy", category: "Legal" })} className={`text-[14px] ${theme.textMuted} ${theme.textMutedHover} transition-colors`}>Privacy Policy</Link>
                            <Link href="/gdpr" target="_blank" rel="noopener noreferrer" onClick={() => trackEvent("Footer Link Clicked", { label: "GDPR", href: "/gdpr", category: "Legal" })} className={`text-[14px] ${theme.textMuted} ${theme.textMutedHover} transition-colors`}>GDPR</Link>
                            <Link href="/refund-policy" target="_blank" rel="noopener noreferrer" onClick={() => trackEvent("Footer Link Clicked", { label: "Refund Policy", href: "/refund-policy", category: "Legal" })} className={`text-[14px] ${theme.textMuted} ${theme.textMutedHover} transition-colors`}>Refund Policy</Link>
                            <Link href="/terms-of-service" target="_blank" rel="noopener noreferrer" onClick={() => trackEvent("Footer Link Clicked", { label: "Terms of Service", href: "/terms-of-service", category: "Legal" })} className={`text-[14px] ${theme.textMuted} ${theme.textMutedHover} transition-colors`}>Terms of Service</Link>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default Footer;
